import React, { useState } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Image,
  Dimensions,
  Linking,
} from "react-native";

const { width } = Dimensions.get("window");

const ContactCard = ({ partnerInfo, employeeInfo }) => {
  // Helper function to map partner info to ContactCard props
  const parsedPartnerInfo = partnerInfo
    ? (() => {
        try {
          return JSON.parse(partnerInfo);
        } catch (error) {
          console.warn("Failed to parse partnerInfo:", error);
          return null;
        }
      })()
    : null;

  const parsedEmployeeInfo = employeeInfo
    ? (() => {
        try {
          return JSON.parse(employeeInfo);
        } catch (error) {
          console.warn("Failed to parse employeeInfo:", error);
          return null;
        }
      })()
    : null;

  const mapPartnerInfo = (partner) => {
    if (!partner) return null;

    const email = partner.partnerEmail?.trim() || "";
    const phone = partner.partnerPhone?.trim() || "";
    const name = partner.name?.trim() || "Partner";
    const biography = partner.biography?.trim() || "";

    return {
      id: partner.id?.toString() || "partner",
      name: name,
      title: "Realtor",
      subtitle: partner.partnerNMLS ? `NMLS #${partner.partnerNMLS}` : "",
      profileImage:
        partner.excludePhoto || !partner.photoPath ? null : partner.photoPath,
      description: biography,
      licensedStates: [], // Partners don't have licensed states in this data
      phone: phone,
      email: email,
      website: partner.websiteUrl?.trim() || partner.sharableLink?.trim() || "",
      textNumber: phone,
      type: "partner",
    };
  };

  // Helper function to map employee info to ContactCard props
  const mapEmployeeInfo = (employee) => {
    if (!employee) return null;

    const displayName = employee.DisplayName?.trim() || "";
    const legalName = employee.LegalName?.trim() || "";
    const name = displayName || legalName || "Loan Officer";
    const displayPhone = employee.DisplayPhone?.trim() || "";
    const legalPhone = employee.LegalPhone?.trim() || "";
    const phone = displayPhone || legalPhone || "";
    const biography = employee.LoBiography?.trim() || "";
    const website = employee.DisplayWebsite?.trim() || "";

    return {
      id: employee.EmpNumber?.toString() || "employee",
      name: name,
      title: "Loan Officer",
      subtitle: employee.LicenseNumber
        ? `License #${employee.LicenseNumber}`
        : "License",
      profileImage: employee.LoPhotoPath || null,
      // employee.ExcludePhoto || !employee.LoPhotoPath
      //   ? null
      //   : employee.LoPhotoPath,
      description: biography,
      licensedStates: [], // Would need additional data for licensed states
      phone: phone,
      email: "", // Email not provided in employee data structure
      website: website,
      textNumber: phone,
      type: "employee",
    };
  };

  // Map the contacts
  const employeeContact = parsedEmployeeInfo
    ? mapEmployeeInfo(parsedEmployeeInfo)
    : null;
  const partnerContact = parsedPartnerInfo
    ? mapPartnerInfo(parsedPartnerInfo)
    : null;

  // Determine primary name for header (prefer employee, fallback to partner)
  const primaryName =
    employeeContact?.name || partnerContact?.name || "Mortgage Professional";

  // Determine primary contact info for collapsed view
  const primaryPhone = employeeContact?.phone || partnerContact?.phone || "";
  const primaryEmail = employeeContact?.email || partnerContact?.email || "";

  // If no contacts, return null
  if (!employeeContact && !partnerContact) {
    return null;
  }

  return (
    <UnifiedContactCard
      employeeContact={employeeContact}
      partnerContact={partnerContact}
      primaryName={primaryName}
      primaryPhone={primaryPhone}
      primaryEmail={primaryEmail}
    />
  );
};

// Unified ContactCard component that displays both contacts in a single card
const UnifiedContactCard = ({
  employeeContact,
  partnerContact,
  primaryName,
  primaryPhone,
  primaryEmail,
}) => {
  const [isEmployeeExpanded, setIsEmployeeExpanded] = useState(false);
  const [isPartnerExpanded, setIsPartnerExpanded] = useState(false);

  const toggleEmployeeExpanded = () => {
    setIsEmployeeExpanded(!isEmployeeExpanded);
  };

  const togglePartnerExpanded = () => {
    setIsPartnerExpanded(!isPartnerExpanded);
  };

  const isAnyExpanded = isEmployeeExpanded || isPartnerExpanded;

  const sanitizeValue = (action, value) => {
    if (!value) return value;

    switch (action) {
      case "phone":
      case "text":
        // Remove all non-numeric characters except + and spaces, then remove spaces
        return value.replace(/[^+\d\s]/g, "").replace(/\s/g, "");
      case "email":
        // Basic email sanitization - trim whitespace
        return value.trim().toLowerCase();
      case "website":
        // Ensure website has proper protocol
        const cleaned = value.trim().toLowerCase();
        if (!cleaned.startsWith("http://") && !cleaned.startsWith("https://")) {
          return `https://${cleaned}`;
        }
        return cleaned;
      default:
        return value;
    }
  };

  const handlePress = (action, value) => {
    try {
      const sanitizedValue = sanitizeValue(action, value);

      switch (action) {
        case "phone":
          Linking.openURL(`tel:${sanitizedValue}`);
          break;
        case "email":
          Linking.openURL(`mailto:${sanitizedValue}`);
          break;
        case "website":
          Linking.openURL(sanitizedValue);
          break;
        case "text":
          Linking.openURL(`sms:${sanitizedValue}`);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error("Error opening URL:", error);
    }
  };

  const renderAvatar = (profileImage, name, size) => {
    const getInitials = (name) => {
      return name
        .split(" ")
        .map((word) => word.charAt(0))
        .join("")
        .toUpperCase();
    };

    if (profileImage) {
      // Add better photo URL handling
      const photoUrl = profileImage;
      //   profileImage.startsWith("http")
      //     ? profileImage
      //     : `https://${profileImage}`;
      return (
        <Image
          source={{ uri: photoUrl }}
          style={[
            styles.avatar,
            { width: size, height: size, borderRadius: size / 2 },
          ]}
          onError={() => console.log("Image load error for:", photoUrl)}
        />
      );
    }
    return (
      <View
        style={[
          styles.avatarFallback,
          { width: size, height: size, borderRadius: size / 2 },
        ]}
      >
        <Text style={[styles.avatarText, { fontSize: size * 0.4 }]}>
          {getInitials(name)}
        </Text>
      </View>
    );
  };

  const renderContactInfo = (icon, label, value, action, contactId) => (
    <TouchableOpacity
      style={styles.contactInfoRow}
      onPress={() => action && handlePress(action, value)}
      disabled={!action}
      testID={`contact-info-${action}-${contactId}`}
    >
      <Text style={styles.contactIcon}>{icon}</Text>
      <Text style={styles.contactInfoText}>
        {label}:{" "}
        <Text style={action ? styles.contactLink : styles.contactValue}>
          {value}
        </Text>
      </Text>
    </TouchableOpacity>
  );

  const renderContactSection = (contact, isExpanded, toggleExpanded) => {
    if (!contact) return null;

    return (
      <View style={styles.contactSection}>
        <TouchableOpacity
          style={styles.contactRow}
          onPress={toggleExpanded}
          activeOpacity={0.7}
          testID={`contact-section-${contact.type}`}
        >
          {renderAvatar(contact.profileImage, contact.name, 50)}

          <View style={styles.contactDetails}>
            <Text style={styles.contactName} numberOfLines={1}>
              {contact.name}
            </Text>
            <Text style={styles.contactTitle} numberOfLines={1}>
              {contact.title}
            </Text>
            {contact.subtitle && (
              <Text style={styles.contactSubtitle} numberOfLines={1}>
                {contact.subtitle}
              </Text>
            )}
          </View>

          <Text style={[styles.chevron, isExpanded && styles.chevronExpanded]}>
            ›
          </Text>
        </TouchableOpacity>

        {isExpanded && (
          <View style={styles.expandedContactInfo}>
            {contact.description && (
              <View style={styles.descriptionSection}>
                <Text style={styles.descriptionText}>
                  {contact.description}
                </Text>
              </View>
            )}

            <View style={styles.contactInfoSection}>
              {contact.phone &&
                renderContactInfo(
                  "Cell",
                  "Cell",
                  contact.phone,
                  "phone",
                  contact.id
                )}
              {contact.email &&
                renderContactInfo(
                  "Email",
                  "Email",
                  contact.email,
                  "email",
                  contact.id
                )}
              {contact.textNumber &&
                renderContactInfo(
                  "Text",
                  "Text",
                  contact.textNumber,
                  "text",
                  contact.id
                )}
              {contact.website &&
                renderContactInfo(
                  "Web",
                  "Website",
                  contact.website,
                  "website",
                  contact.id
                )}
            </View>
          </View>
        )}
      </View>
    );
  };

  if (!isAnyExpanded) {
    // Collapsed View
    return (
      <View style={styles.card}>
        {/* Header */}
        <Text style={styles.cardHeader}>
          My name is {primaryName} and I am your mortgage professional.
        </Text>

        <View style={styles.cardContent}>
          {/* Employee Section */}
          {employeeContact &&
            renderContactSection(
              employeeContact,
              isEmployeeExpanded,
              toggleEmployeeExpanded
            )}

          {/* Separator Line */}
          {employeeContact && partnerContact && (
            <View style={styles.separator} />
          )}

          {/* Partner Section */}
          {partnerContact &&
            renderContactSection(
              partnerContact,
              isPartnerExpanded,
              togglePartnerExpanded
            )}
        </View>
      </View>
    );
  }

  // Expanded View
  return (
    <View style={styles.expandedCard}>
      <View style={styles.expandedContent}>
        {/* Close Button */}
        <View style={styles.expandedHeader}>
          <View style={styles.spacer} />
          <TouchableOpacity
            style={styles.closeButton}
            onPress={() => {
              setIsEmployeeExpanded(false);
              setIsPartnerExpanded(false);
            }}
            activeOpacity={0.7}
            testID="contact-card-close"
          >
            <Text style={styles.closeButtonText}>×</Text>
          </TouchableOpacity>
        </View>

        {/* Header */}
        <Text style={styles.expandedCardHeader}>
          My name is {primaryName} and I am your mortgage professional.
        </Text>

        {/* Employee Section */}
        {employeeContact &&
          renderContactSection(
            employeeContact,
            isEmployeeExpanded,
            toggleEmployeeExpanded
          )}

        {/* Separator Line */}
        {employeeContact && partnerContact && <View style={styles.separator} />}

        {/* Partner Section */}
        {partnerContact &&
          renderContactSection(
            partnerContact,
            isPartnerExpanded,
            togglePartnerExpanded
          )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  // Card Styles
  card: {
    backgroundColor: "white",
    borderRadius: 8,
    maxWidth: width > 768 ? 700 : width - 32, // 700px on desktop, responsive on mobile
    width: "100%",
    alignSelf: "stretch",
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.08,
    shadowRadius: 2,
    elevation: 2,
    padding: 16,
    marginTop: 24,
    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
  },

  cardHeader: {
    fontSize: 18,
    fontWeight: "700",
    color: "#1f2937", // text-gray-800
    marginBottom: 16,
    lineHeight: 24,
    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
  },

  cardContent: {
    // No additional styles needed for touchable container
  },

  contactSection: {
    // Container for each contact section
  },

  contactRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 8,
  },

  separator: {
    height: 1,
    backgroundColor: "#e5e7eb", // border-gray-200
    marginVertical: 16,
  },

  avatar: {
    marginRight: 12,
  },
  avatarFallback: {
    backgroundColor: "#4A90E2",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },
  avatarText: {
    color: "white",
    fontWeight: "bold",
  },

  contactDetails: {
    flex: 1,
  },
  contactName: {
    fontSize: 18,
    fontWeight: "700",
    color: "#1f2937", // text-gray-800
    marginBottom: 2,
    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
  },
  contactTitle: {
    fontSize: 14,
    fontWeight: "500",
    color: "#6b7280", // text-gray-500
    marginBottom: 1,
    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
  },
  contactSubtitle: {
    fontSize: 12,
    fontWeight: "400",
    color: "#9ca3af", // text-gray-400
    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
  },
  chevron: {
    fontSize: 18,
    color: "#9ca3af", // text-gray-400
    paddingLeft: 8,
    transform: [{ rotate: "0deg" }],
  },
  chevronExpanded: {
    transform: [{ rotate: "90deg" }],
  },

  // Expanded Card Styles
  expandedCard: {
    backgroundColor: "white",
    borderRadius: 12,
    margin: 16,
    marginTop: 24,
    maxWidth: width > 768 ? 700 : width - 32, // 700px on desktop, responsive on mobile
    width: "100%",
    alignSelf: "flex-start",
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 8,
  },
  expandedContent: {
    padding: 24,
    gap: 24,
  },
  expandedHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    width: "100%",
  },
  spacer: {
    flex: 1,
  },
  closeButton: {
    width: 32,
    height: 32,
    alignItems: "center",
    justifyContent: "center",
  },
  closeButtonText: {
    fontSize: 24,
    color: "#9ca3af", // text-gray-400
    fontWeight: "bold",
    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
  },

  expandedCardHeader: {
    fontSize: 20,
    fontWeight: "700",
    color: "#1f2937", // text-gray-800
    lineHeight: 28,
    textAlign: "left",
    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
  },

  expandedContactInfo: {
    marginTop: 16,
    paddingLeft: 62, // Align with contact details
  },

  descriptionSection: {
    marginBottom: 16,
  },
  descriptionText: {
    fontSize: 14,
    color: "#374151", // text-gray-700
    lineHeight: 20,
    textAlign: "left",
    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
  },

  contactInfoSection: {
    gap: 8,
  },
  contactInfoRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 4,
  },
  contactIcon: {
    fontSize: 12,
    fontWeight: "600",
    color: "#374151", // text-gray-700
    marginRight: 12,
    width: 120, // 120px as requested
    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
  },
  contactInfoText: {
    fontSize: 14,
    color: "#374151", // text-gray-700
    flex: 1,
    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
  },
  contactLink: {
    color: "#2563eb", // blue-600
    textDecorationLine: "underline",
    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
  },
  contactValue: {
    color: "#374151", // text-gray-700
  },
});

export default ContactCard;
